import { t } from '@lingui/core/macro';
import { useCallback, useMemo } from 'react';

import type { OptionType } from '@/api/common';
import {
  CategoryFilter,
  ComboboxPickerFilter,
  encodeFilterFieldName,
  FilterField,
  useFilterForm,
} from '@/components/filters';
import type { FilterOperatorMulti } from '@/components/InstantSearch';
import { KeywordMatchTypeTag } from '@/features/tags';
import type { FilterComponentProps } from '@/providers/TableState';

type Props = FilterComponentProps & {
  mode?: 'multiple';
};

type MatchTypeOption = OptionType & {
  matchType: 'Broad' | 'Phrase' | 'Exact';
};

const KeywordMatchTypeFilter = ({
  name,
  label,
  infoPopover,
  compact = false,
  mode = 'multiple',
  ...props
}: Props) => {
  const form = useFilterForm();
  const matchTypeOptions: MatchTypeOption[] = useMemo(
    () => [
      { id: 'Broad', name: t`Broad`, matchType: 'Broad' },
      { id: 'Phrase', name: t`Phrase`, matchType: 'Phrase' },
      { id: 'Exact', name: t`Exact`, matchType: 'Exact' },
    ],
    [],
  );

  const renderOption = useCallback((option: MatchTypeOption) => {
    return <KeywordMatchTypeTag matchType={option.matchType} />;
  }, []);

  return (
    <form.AppField name={encodeFilterFieldName(name)}>
      {(field) => (
        <FilterField
          data-testid="keyword-match-type-filter"
          label={label}
          infoPopover={infoPopover}
          hideLabel={!compact}
        >
          {compact ? (
            <ComboboxPickerFilter
              {...props}
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              mode={mode}
              singlePlaceholder={t`Select Match Type...`}
              multiplePlaceholder={t`Select Match Types...`}
              multipleLoadingPlaceholder={t`Loading Match Types...`}
              filteredItems={matchTypeOptions}
              renderOption={renderOption}
              notFoundContent={t`No match types were found...`}
            />
          ) : (
            <CategoryFilter
              {...props}
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              selectionMode={mode}
              options={matchTypeOptions}
              renderOption={renderOption}
            />
          )}
        </FilterField>
      )}
    </form.AppField>
  );
};

export default KeywordMatchTypeFilter;
